// src/components/TPP/TPPCell/components/CellActionsMenu.tsx
import React, { ElementType, MouseEvent, useCallback, useState, useRef } from 'react'
import { useQueryClient } from 'react-query'
import { IconButton, ClickAwayListener, Fade, Popper, Stack, Typography, CircularProgress } from '@mui/material'
import { ReactComponent as CommentActionIcon } from '../../../../../assets/images/comment_icon.svg'
import { ReactComponent as CloneActionIcon } from '../../../../../assets/images/cloneto_icon.svg'
import { ReactComponent as HighlightActionIcon } from '../../../../../assets/images/differentiating_icon.svg'
import { ReactComponent as LockActionIcon } from '../../../../../assets/images/lock_icon.svg'
import { ReactComponent as UnlockActionIcon } from '../../../../../assets/images/actions/unlock.svg'
import { ReactComponent as DeleteActionIcon } from '../../../../../assets/images/trash_icon.svg'
import { ReactComponent as ActionsMenuIcon } from '../../../../../assets/images/actions/actions.svg'
import SvgIcon from '@mui/material/SvgIcon'
import { isCellLocked } from '../utils/CellLockUtil'
import { useToggleTPPCellLockMutation } from '../queries/useToggleTPPCellLockMutation'
import { useToggleTPPCellHighlightMutation } from '../queries/useToggleTPPCellHighlightMutation'
import { useDeleteTPPCellMutation } from '../queries/useDeleteTPPCellMutation'
import { useCloneTPPCellMutation } from '../queries/useCloneTPPCellMutation'

import { TPPCellCommentsPopover } from '../../../../../shared/components/Comments/variants/PopoverComments/TPPCellCommentsPopover'
import { useEscapeKey } from '../../../../../shared/hooks/useEscapeKey'

type ActionId = 'comment' | 'clone' | 'highlight' | 'lock' | 'delete'
type Action = {
    id: ActionId
    icon: ElementType
    name: string
    handler?: () => void | Promise<void>
    loading?: boolean
    disabled?: boolean
}

type ColumnOption = { id: string; label: string }

export default function CellActionsMenu({
    cell,
    dispatch,
    visibleColumns,
    occupiedColumnIds,
    isMovePending,
    onOpenChange,
    onlyComment,
}: {
    cell: any
    dispatch: any
    visibleColumns: ColumnOption[]
    occupiedColumnIds: string[]
    isMovePending?: boolean
    onOpenChange?: (open: boolean) => void
    onlyComment?: boolean
}) {
    const queryClient = useQueryClient()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLElement | null>(null)

    const [cloneAnchorEl, setCloneAnchorEl] = useState<HTMLElement | null>(null)
    const [commentsOpen, setCommentsOpen] = useState(false)
    const [commentsAnchorEl, setCommentsAnchorEl] = useState<HTMLElement | null>(null)

    // Track if submenus are open using a ref to avoid stale closure issues
    const hasSubmenuOpenRef = useRef(false)
    hasSubmenuOpenRef.current = !!deleteAnchorEl || !!cloneAnchorEl || commentsOpen

    // Try to infer the parent TPP id from the cell

    const tppId = cell.tppId

    /*const cloneTargetColumns = useMemo(
        () =>
            visibleColumns.map((col: any) => ({
                id: col._id,
                label: col.title,
            })),
        [visibleColumns]
    )*/

    const locked = isCellLocked(cell?.locked)

    const actionsButtonRef = useRef<HTMLButtonElement | null>(null)

    const openComments = (anchor?: HTMLElement | null) => {
        // Prefer anchoring to the clicked row; fall back to the actions button.
        const btn = actionsButtonRef.current
        if (!btn) return

        setCommentsAnchorEl(btn)
        setCommentsOpen(true)

        setAnchorEl(btn)
        onOpenChange?.(true)

        setCloneAnchorEl(null)
        //setAnchorEl(null)
        setDeleteAnchorEl(null)
    }

    const closeComments = () => {
        setCommentsOpen(false)
        setCommentsAnchorEl(null)
    }

    const handleCommentPosted = useCallback(() => {
        queryClient.setQueryData(['tppCells', tppId], (old: any[]) => {
            if (!old) return []
            return old.map((c: any) => (c._id === cell._id ? { ...c, hasComment: true } : c))
        })
    }, [queryClient, cell?._id, tppId])

    const handleCommentRemoved = useCallback(() => {
        const comments = queryClient.getQueryData(['comments', String(cell?._id), 'tppCell'])
        const stillHasComments = Array.isArray(comments) && comments.length > 0

        queryClient.setQueryData(['tppCells', tppId], (old: any[]) => {
            if (!old) return []
            return old.map((c: any) => (c._id === cell._id ? { ...c, hasComment: stillHasComments } : c))
        })
    }, [queryClient, cell?._id, tppId])

    const { mutateAsync: toggleTPPCellLock, isLoading: isLocking } = useToggleTPPCellLockMutation()
    const { mutateAsync: deleteTPPCell, isLoading: isDeleting } = useDeleteTPPCellMutation()
    const { mutateAsync: toggleTPPCellHighlight, isLoading: isHighlighting } = useToggleTPPCellHighlightMutation()
    const { mutateAsync: cloneTPPCell, isLoading: isCloning, variables: cloningVariables } = useCloneTPPCellMutation()

    const hasChildren = Array.isArray(cell?.childClones) && cell.childClones.length > 0
    const hasCloneTargets = visibleColumns.some(
        (option) => option.id !== cell.columnId && !(occupiedColumnIds || []).includes(option.id)
    )

    const baseCommentAction: Action = {
        id: 'comment',
        name: 'Comment',
        icon: CommentActionIcon,
        handler: () => {},
    }

    // @ts-expect-error @TODO tpp - check types
    const actions: Action[] = onlyComment
        ? [baseCommentAction]
        : locked
          ? [
                {
                    id: 'lock',
                    name: 'Unlock',
                    icon: UnlockActionIcon,
                    handler: async () => {
                        if (tppId && cell?._id) {
                            await toggleTPPCellLock({ cellId: cell._id, tppId })
                        }
                    },
                    loading: isLocking,
                },
            ]
          : [
                baseCommentAction,
                ...(hasCloneTargets
                    ? [
                          {
                              id: 'clone',
                              name: 'Clone to',
                              icon: CloneActionIcon,
                              loading: isCloning,
                          },
                      ]
                    : []),
                {
                    id: 'highlight',
                    name: cell.isHighlighted ? 'Unhighlight' : 'Highlight',
                    icon: HighlightActionIcon,
                    handler: async () => {
                        if (tppId && cell?._id) {
                            await toggleTPPCellHighlight({ cellId: cell._id, tppId, highlight: !cell.isHighlighted })
                            handleClose()
                        }
                    },
                    loading: isHighlighting,
                },
                {
                    id: 'lock',
                    name: 'Lock',
                    icon: LockActionIcon,
                    handler: async () => {
                        if (tppId && cell?._id) {
                            await toggleTPPCellLock({ cellId: cell._id, tppId })
                        }
                    },
                    loading: isLocking,
                },
                ...(hasChildren
                    ? []
                    : [
                          {
                              id: 'delete',
                              name: 'Delete',
                              icon: DeleteActionIcon,
                              loading: isDeleting,
                          },
                      ]),
            ]

    const handleCloseAll = useCallback(() => {
        setAnchorEl(null)
        setCloneAnchorEl(null)
        setDeleteAnchorEl(null)
        setCommentsOpen(false)
        setCommentsAnchorEl(null)
        onOpenChange?.(false)
    }, [onOpenChange])

    const hasOpenMenu = !!anchorEl || !!cloneAnchorEl || !!deleteAnchorEl || commentsOpen
    useEscapeKey(handleCloseAll, hasOpenMenu)

    const handleToggle = (event: MouseEvent<HTMLElement>) => {
        const next = anchorEl ? null : event.currentTarget
        setAnchorEl(next)
        onOpenChange?.(!!next)
    }

    const handleClose = handleCloseAll

    const renderActionRow = (action: Action) => {
        const handleOnClick = async (event: MouseEvent<HTMLElement>) => {
            if (action.disabled) return
            try {
                if (action.id === 'clone') {
                    setDeleteAnchorEl(null)
                    setCloneAnchorEl(event.currentTarget)
                    return
                }
                if (action.id === 'delete') {
                    setCloneAnchorEl(null)
                    setDeleteAnchorEl(event.currentTarget)
                    return
                }
                if (action.id === 'comment') {
                    openComments()
                    return
                }

                await action.handler?.()

                // @ts-expect-error @TODO tpp - check types
                if (action.id !== 'clone' && action.id !== 'delete') {
                    handleClose()
                }
            } catch (_error) {
                // keep open
            }
        }

        return (
            <Stack
                onClick={handleOnClick}
                direction="row"
                gap={1.5}
                px={1.5}
                py={0.75}
                alignItems="center"
                sx={{
                    color: action.disabled
                        ? 'figma.grayscale.tier4'
                        : action.loading
                          ? 'figma.primary'
                          : 'figma.grayscale.textHier2',
                    '&:hover': {
                        color: action.disabled ? 'figma.grayscale.tier4' : 'figma.primary',
                    },
                    opacity: action.disabled ? 0.5 : 1,
                    cursor: action.disabled ? 'not-allowed' : 'pointer',
                }}
            >
                <SvgIcon
                    component={action.loading ? () => <CircularProgress size={24} color="primary" /> : action.icon}
                    inheritViewBox
                    fontSize="small"
                    color="inherit"
                    sx={{ fontSize: '24px' }}
                />
                <Typography variant="14x400" color="inherit" whiteSpace="nowrap">
                    {action.name}
                </Typography>
            </Stack>
        )
    }

    const handleConfirmDelete = async () => {
        if (!cell?._id || !tppId) return
        try {
            await deleteTPPCell({ cellId: cell._id, tppId })
            handleClose()
        } catch (_error) {
            // keep open
        }
    }

    return (
        <>
            <IconButton
                aria-label="Cell actions"
                size="small"
                ref={actionsButtonRef}
                onClick={handleToggle}
                disabled={isMovePending}
                sx={{
                    p: 0.5,
                    borderRadius: '50%',
                    color: locked ? 'primary.main' : anchorEl ? 'primary.main' : 'figma.grayscale.tier2',
                }}
            >
                <SvgIcon
                    component={ActionsMenuIcon}
                    inheritViewBox
                    sx={{
                        fontSize: '20px',
                    }}
                />
            </IconButton>

            <Popper
                open={!!anchorEl}
                anchorEl={anchorEl}
                placement="bottom-start"
                sx={(theme) => ({ zIndex: theme.zIndex.modal + 10 })}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener
                        onClickAway={() => {
                            // Don't close main menu if a submenu is open
                            if (hasSubmenuOpenRef.current) return
                            handleClose()
                        }}
                    >
                        <Fade {...TransitionProps} in={!!anchorEl} timeout={200} unmountOnExit mountOnEnter>
                            <Stack
                                direction="column"
                                py={0.75}
                                boxShadow="0px 4px 8px 0px rgba(0, 0, 0, 0.20)"
                                borderRadius="12px"
                                bgcolor="figma.grayscale.tier7"
                                ml={-1.25}
                            >
                                {actions.map((action) => renderActionRow(action))}
                            </Stack>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>

            <Popper
                open={!!cloneAnchorEl}
                anchorEl={cloneAnchorEl}
                placement="right-start"
                sx={(theme) => ({ zIndex: theme.zIndex.modal + 10 })}
                modifiers={[
                    {
                        name: 'offset',
                        options: { offset: [-4, 0] },
                    },
                ]}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener
                        onClickAway={() => {
                            if (isCloning) return
                            setCloneAnchorEl(null)
                        }}
                    >
                        <Fade {...TransitionProps} in={!!cloneAnchorEl} timeout={200} unmountOnExit mountOnEnter>
                            <Stack
                                direction="column"
                                py={0.75}
                                boxShadow="0px 4px 8px 0px rgba(0, 0, 0, 0.20)"
                                borderRadius="12px"
                                bgcolor="figma.grayscale.tier7"
                            >
                                {visibleColumns.map((option) => {
                                    const disabled =
                                        option.id === cell.columnId || (occupiedColumnIds || []).includes(option.id)
                                    return (
                                        <Stack
                                            key={option.id}
                                            direction="row"
                                            gap={1.5}
                                            px={1.5}
                                            py={0.75}
                                            alignItems="center"
                                            onClick={async () => {
                                                if (disabled || isCloning || !cell?._id || !tppId) return
                                                try {
                                                    await cloneTPPCell({ cellId: cell._id, tppId, columnId: option.id })
                                                    handleClose()
                                                } catch (_error) {
                                                    // keep open
                                                }
                                            }}
                                            sx={{
                                                color: disabled
                                                    ? 'figma.grayscale.tier5'
                                                    : isCloning && cloningVariables.columnId === option.id
                                                      ? 'figma.primary'
                                                      : 'figma.grayscale.textHier2',
                                                '&:hover': {
                                                    color: disabled ? 'figma.grayscale.tier5' : 'figma.primary',
                                                },
                                                opacity: isCloning && !disabled ? 0.6 : 1,
                                                cursor: disabled || isCloning ? 'not-allowed' : 'pointer',
                                            }}
                                        >
                                            <Typography variant="14x400" color="inherit" whiteSpace="nowrap">
                                                {option.label}
                                            </Typography>
                                        </Stack>
                                    )
                                })}
                            </Stack>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>

            <Popper
                open={!!deleteAnchorEl}
                anchorEl={deleteAnchorEl}
                placement="right-start"
                modifiers={[
                    {
                        name: 'offset',
                        options: { offset: [-4, 0] },
                    },
                ]}
                sx={(theme) => ({ zIndex: theme.zIndex.modal + 10 })}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={() => !isDeleting && setDeleteAnchorEl(null)}>
                        <Fade {...TransitionProps} in={!!deleteAnchorEl} timeout={200} unmountOnExit mountOnEnter>
                            <Stack
                                direction="column"
                                py={0.75}
                                boxShadow="0px 4px 8px 0px rgba(0, 0, 0, 0.20)"
                                borderRadius="12px"
                                bgcolor="figma.grayscale.tier7"
                            >
                                <Stack
                                    direction="row"
                                    gap={1.5}
                                    px={1.5}
                                    py={0.75}
                                    alignItems="center"
                                    onClick={async () => {
                                        if (isDeleting) return
                                        await handleConfirmDelete()
                                    }}
                                    sx={{
                                        color: isDeleting ? 'figma.primary' : 'figma.accentColors.red',
                                        '&:hover': {
                                            color: isDeleting ? 'figma.primary' : 'figma.primary',
                                        },
                                        cursor: isDeleting ? 'progress' : 'pointer',
                                    }}
                                >
                                    <Typography variant="14x400" color="inherit" whiteSpace="nowrap">
                                        Confirm
                                    </Typography>
                                </Stack>
                                <Stack
                                    direction="row"
                                    gap={1.5}
                                    px={1.5}
                                    py={0.75}
                                    alignItems="center"
                                    onClick={() => {
                                        if (isDeleting) return
                                        setDeleteAnchorEl(null)
                                    }}
                                    sx={{
                                        color: 'figma.grayscale.textHier2',
                                        '&:hover': {
                                            color: 'figma.primary',
                                        },
                                        opacity: isDeleting ? 0.4 : 1,
                                        cursor: isDeleting ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    <Typography variant="14x400" color="inherit" whiteSpace="nowrap">
                                        Cancel
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>
            {commentsOpen && (
                <TPPCellCommentsPopover
                    open={commentsOpen}
                    anchorEl={commentsAnchorEl}
                    onClose={closeComments}
                    dataId={String(cell?._id)}
                    dataType={'tppCell'}
                    onCommentPosted={handleCommentPosted}
                    onCommentRemoved={handleCommentRemoved}
                />
            )}
        </>
    )
}
